import React from 'react';
import { createRoot } from 'react-dom/client';

import './config/i18n';
import './loadTranslations';
import App from './app';
import './scss/application.scss';

const root = createRoot(document.getElementById('root'));

// The app stylesheets are loaded off the critical rendering path so the static shell
// in index.html can paint without them (see `asyncCssPlugin` in vite.config.js).
// They are ~24KB against the ~470KB of JS that has to arrive before this module can
// run at all, so in practice they are always applied long before this line — but
// mounting waits on them anyway, because the single failure mode of async CSS is a
// first render with no styles, and ruling it out costs nothing here. The cap means a
// stylesheet that never answers delays the app by that much instead of forever.
const STYLES_MAX_WAIT = 3000;

const whenStylesReady = () => {
  // Absent in dev and in the `<noscript>` fallback, where the links are ordinary
  // render-blocking stylesheets and there is nothing to wait for.
  const pending = Array.from(document.querySelectorAll('link[data-pj-css]')).filter(
    link => !link.dataset.pjCssLoaded
  );

  if (!pending.length) {
    return Promise.resolve();
  }

  return Promise.race([
    Promise.all(
      pending.map(
        link =>
          new Promise(resolve => {
            // A stylesheet that fails to load must not hold the app back: the
            // `<img>` fallbacks and the shell already cover a broken deploy.
            link.addEventListener('load', resolve, { once: true });
            link.addEventListener('error', resolve, { once: true });
          })
      )
    ),
    new Promise(resolve => setTimeout(resolve, STYLES_MAX_WAIT))
  ]);
};

whenStylesReady().then(() => root.render(<App />));
